import { memo, useCallback, useRef } from 'react';

import { ListTodoIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { ButtonGroup } from '@air/primitive-button-group';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import {
  WorkspaceAgentAirMark,
  type WorkspaceAgentAirMarkHandle,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentAirMark';
import { agenticStartTaskSegment } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentStartTaskButtonVariants';
import styles from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentStartTaskCta.module.css';
import { WORKSPACE_AGENT_PANEL_ELEMENT_ID } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';

export type WorkspaceAgentHeaderStartTaskSplitButtonProps = {
  isShowingChat: boolean;
  isShowingTasks: boolean;
  onOpenTasks: () => void;
  onStartTask: () => void;
};

export const WorkspaceAgentHeaderStartTaskSplitButton = memo(
  ({ isShowingChat, isShowingTasks, onOpenTasks, onStartTask }: WorkspaceAgentHeaderStartTaskSplitButtonProps) => {
    // Tooltip copy only: the accessible name stays "Tasks" because `aria-pressed` already carries
    // the state, and a name that describes the next action would announce it twice.
    const tasksTooltip = isShowingTasks ? 'Close tasks' : 'Open tasks';
    const markRef = useRef<WorkspaceAgentAirMarkHandle>(null);

    const handleStartTaskPointerEnter = useCallback(() => {
      markRef.current?.replay();
    }, []);

    return (
      // No `overflow-hidden`: the UA `:focus-visible` outline is the segments' only keyboard focus
      // indicator, and a clipping wrapper that hugs them would cut it off on every outer edge.
      <ButtonGroup className={cn(styles.agenticBlueCta, 'inline-flex h-8 w-fit rounded-8')}>
        <Button
          appearance="filled"
          // `aria-expanded`, not `aria-pressed`: `showChat` is one-way (it never closes the panel), so
          // this is a disclosure of the chat view rather than a toggle.
          aria-controls={isShowingChat ? WORKSPACE_AGENT_PANEL_ELEMENT_ID : undefined}
          aria-expanded={isShowingChat}
          className={agenticStartTaskSegment({
            className: 'gap-1.5 border-r border-r-white/20',
            isPressed: isShowingChat,
          })}
          color="blue"
          data-testid="WORKSPACE_CHROME_AGENT_START_TASK_BUTTON"
          onClick={onStartTask}
          onPointerEnter={handleStartTaskPointerEnter}
          size="medium"
        >
          <WorkspaceAgentAirMark autoplayOnMount ref={markRef} />
          Start task
        </Button>
        <Tooltip label={tasksTooltip} side="bottom">
          <IconButton
            adsIcon
            appearance="filled"
            aria-controls={isShowingTasks ? WORKSPACE_AGENT_PANEL_ELEMENT_ID : undefined}
            aria-pressed={isShowingTasks}
            className={agenticStartTaskSegment({ isPressed: isShowingTasks })}
            color="blue"
            data-testid="WORKSPACE_CHROME_AGENT_TASKS_BUTTON"
            icon={ListTodoIcon}
            label="Tasks"
            onClick={onOpenTasks}
            size="medium"
          />
        </Tooltip>
      </ButtonGroup>
    );
  },
);

WorkspaceAgentHeaderStartTaskSplitButton.displayName = 'WorkspaceAgentHeaderStartTaskSplitButton';
